import { CircleCheckIcon, CircleSlashIcon, CircleXIcon, ClockIcon, LoaderCircleIcon, RotateCcwIcon, SquareIcon, TriangleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { Turn } from "../../../../shared/api.ts";
import { Logo } from "@/components/brand/logo.tsx";
import { MarkdownText } from "@/components/common/markdown.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { formatCodesSafely } from "@/lib/codes.ts";
import { useNow } from "@/lib/hooks.ts";
import { isActiveStatus, trackSegments, turnElapsedMs } from "@/lib/turns.ts";
import { cn } from "@/lib/utils.ts";
import { PhaseTrack } from "./phase-track.tsx";
import { StepTimeline } from "./step-timeline.tsx";
import { UsageChip } from "./usage-chip.tsx";

/** The label of the step running now, or of the latest one. */
function currentLabel(turn: Turn): string | null {
  const steps = turn.steps;
  const running = [...steps].reverse().find((step) => step.status === "running");
  const step = running ?? steps[steps.length - 1];
  return step ? formatCodesSafely(step.label) : null;
}

export type TurnActions = {
  onRetry?: (turnId: string) => void;
  onStop?: (turnId: string) => void;
  retrying?: boolean;
  stopping?: boolean;
};

function TurnStatusLine({ turn, now }: { turn: Turn; now: number }) {
  const { t, fmt } = useI18n();
  const elapsed = turnElapsedMs(turn, now);
  let icon: ReactNode;
  let label: ReactNode;
  let extra: ReactNode = null;

  switch (turn.status) {
    case "queued":
      icon = <ClockIcon className="size-4 text-muted-foreground" aria-hidden />;
      label = t.turn.queued;
      extra = <span className="truncate text-[12.5px] text-muted-foreground">{t.turn.queuedHint}</span>;
      break;
    case "running":
      icon = <LoaderCircleIcon className="size-4 animate-spin text-primary" aria-hidden />;
      label = t.turn.running;
      extra = (
        <>
          <span className="code-mono min-w-[3.25rem] text-[13px] font-medium text-foreground" aria-label={t.turn.elapsed}>
            {fmt.clock(elapsed ?? 0)}
          </span>
          {turn.phase ? (
            <span className="truncate text-[12.5px] text-muted-foreground">
              <span aria-hidden>· </span>
              {t.phases.raw[turn.phase]}
            </span>
          ) : null}
        </>
      );
      break;
    case "succeeded":
      icon = <CircleCheckIcon className="size-4 text-success" aria-hidden />;
      label = t.turn.doneIn(fmt.duration(elapsed));
      break;
    case "failed":
      icon = <CircleXIcon className="size-4 text-danger" aria-hidden />;
      label = t.turn.failed;
      break;
    case "interrupted":
      icon = <TriangleAlertIcon className="size-4 text-warning" aria-hidden />;
      label = t.turn.interrupted;
      break;
    case "cancelled":
      icon = <CircleSlashIcon className="size-4 text-muted-foreground" aria-hidden />;
      label = t.turn.cancelled;
      break;
  }

  return (
    <div className="flex min-w-0 items-center gap-2" aria-live={turn.status === "running" ? "off" : "polite"}>
      {icon}
      <span className="shrink-0 text-[13.5px] font-semibold text-foreground">{label}</span>
      {extra}
    </div>
  );
}

function TurnProblem({ turn, actions, readOnly }: { turn: Turn; actions: TurnActions; readOnly: boolean }) {
  const { t } = useI18n();
  if (turn.status !== "failed" && turn.status !== "interrupted" && turn.status !== "cancelled") return null;
  const hint =
    turn.status === "failed" ? t.turn.failedHint : turn.status === "interrupted" ? t.turn.interruptedHint : t.turn.cancelledHint;
  const tone = turn.status === "failed" ? "danger" : turn.status === "interrupted" ? "warning" : "neutral";
  return (
    <div
      className={cn(
        "mx-4 mb-3 flex flex-col gap-3 rounded-lg border px-3 py-2.5 text-[13px] sm:flex-row sm:items-center",
        tone === "danger" && "border-danger-border bg-danger-soft",
        tone === "warning" && "border-warning-border bg-warning-soft",
        tone === "neutral" && "bg-muted/60",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className={cn("font-medium", tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-foreground")}>{hint}</p>
        {turn.error ? <p className="mt-0.5 break-words text-muted-foreground">{turn.error}</p> : null}
      </div>
      {!readOnly && actions.onRetry ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start sm:self-center"
          disabled={actions.retrying}
          onClick={() => actions.onRetry?.(turn.id)}
        >
          <RotateCcwIcon aria-hidden />
          {actions.retrying ? t.turn.retrying : t.turn.retry}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * One agent turn: status and timing, the phase track, usage, the step timeline, and the reply.
 * While the turn runs, the timer ticks, the draft reply streams with a caret, and Stop is offered.
 */
export function TurnCard({
  turn,
  replyText,
  actions = {},
  readOnly = false,
}: {
  turn: Turn;
  /** The assistant message of the turn, when it exists. */
  replyText: string | null;
  actions?: TurnActions;
  readOnly?: boolean;
}) {
  const { t } = useI18n();
  const active = isActiveStatus(turn.status);
  const running = turn.status === "running";
  const now = useNow(active);
  const segments = trackSegments(turn.steps, turn, now);
  const showTrack = turn.steps.length > 0 || running;
  const draft = active ? turn.replyDraft : null;
  const reply = replyText ?? draft ?? (active ? null : turn.replyDraft);
  const elapsed = turnElapsedMs(turn, now);

  return (
    <article
      className={cn(
        "overflow-hidden rounded-xl border bg-card shadow-[0_1px_3px_rgba(15,27,45,0.05)] transition-[border-color,box-shadow]",
        running && "border-primary/35 shadow-[0_0_0_3px_color-mix(in_oklab,var(--primary)_8%,transparent)]",
      )}
      aria-busy={active}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pt-3 pb-3">
        <Logo size={20} className="text-primary dark:text-foreground" title={t.turn.mark} />
        <div className="min-w-0 flex-1">
          <TurnStatusLine turn={turn} now={now} />
        </div>
        {turn.steps.length > 0 || turn.usage.inputTokens > 0 ? (
          <UsageChip usage={turn.usage} accessMode={turn.accessMode} model={turn.model} />
        ) : null}
      </header>

      {showTrack ? (
        <div className="px-4 pb-3.5">
          <PhaseTrack segments={segments} />
        </div>
      ) : null}

      <TurnProblem turn={turn} actions={actions} readOnly={readOnly} />

      <div className="border-t px-3 py-1.5 sm:px-4">
        <StepTimeline steps={turn.steps} calls={turn.calls} running={active} totalMs={elapsed} now={now} />
      </div>

      {reply ? (
        <div className="border-t px-4 py-4 sm:px-5">
          <MarkdownText text={reply} streaming={Boolean(draft) && !replyText} />
        </div>
      ) : null}

      {active && !readOnly && actions.onStop ? (
        <footer className="flex items-center justify-between gap-3 border-t bg-muted/30 px-4 py-2.5">
          <span className="min-w-0 truncate text-[12.5px] text-muted-foreground" aria-live="polite">
            {turn.status === "queued" ? (
              t.turn.queuedHint
            ) : (
              <>
                <span className="font-semibold text-foreground/80">{t.turn.phaseNow}: </span>
                {draft ? t.turn.writing : currentLabel(turn) ?? t.turn.starting}
              </>
            )}
          </span>
          <Button variant="outline" size="sm" disabled={actions.stopping} onClick={() => actions.onStop?.(turn.id)}>
            <SquareIcon className="size-3 fill-current" aria-hidden />
            {actions.stopping ? t.turn.stopping : t.turn.stop}
          </Button>
        </footer>
      ) : null}
    </article>
  );
}
